/* ----

  This JS is taken from this article - https://www.24a11y.com/2019/select-your-poison-part-2/
  It is also modified to be used as a way to decorate select dropdowns and make them
  accessible using keyboards.

  How to use it:
  Add js-combobox class to the <select> element you would like to replace with the combobox.
  Hide your <select> element as well as corresponding <label> element with display: none;.

  On load, this libary will create a new <label> element and a combobox element which consists
  of an input element and a listbox.

  The new <label> element will have select label's aria-label.
  The new combobox element will have select options' values and text.

  On option select, this libary will also set your hidden <select> element's val so it
  can be used further.

---- */

export class ComboBox {
  constructor(form, priceDisplay) {
    this.keys = {
      Backspace: "Backspace",
      Clear: "Clear",
      Down: "ArrowDown",
      End: "End",
      Enter: "Enter",
      Escape: "Escape",
      Home: "Home",
      Left: "ArrowLeft",
      PageDown: "PageDown",
      PageUp: "PageUp",
      Right: "ArrowRight",
      Space: " ",
      Tab: "Tab",
      Up: "ArrowUp",
    };
    this.menuActions = {
      Close: 0,
      CloseSelect: 1,
      First: 2,
      Last: 3,
      Next: 4,
      Open: 5,
      Previous: 6,
      Select: 7,
      Space: 8,
      Type: 9,
    };
    this.formEl = form;
    this.priceDisplay = priceDisplay;
    this.wrapperEl = false;
    this.comboEl = false;
    this.inputEl = false;
    this.listboxEl = false;
    this.optionId = false;
    this.sellingPlanInput = false;

    this.el = false;
    this.inputEl = false;
    this.selectEl = false;
    this.idBase = false;
    this.options = false;
    this.optionValues = false;
    this.activeIndex = 1;
    this.open = false;
    this.allLemmings = false;

    this._init();
  }

  // return an array of exact option name matches from a comma-separated string
  _findMatches = (options, search) => {
    const names = search.split(",");
    return names
      .map((name) => {
        const match = options.filter(
          (option) => name.trim().toLowerCase() === option.toLowerCase()
        );
        return match.length > 0 ? match[0] : null;
      })
      .filter((option) => option !== null);
  };

  // return combobox action from key press
  _getActionFromKey = (key, menuOpen) => {
    // handle opening when closed
    if (!menuOpen && key === this.keys.Down) {
      return this.menuActions.Open;
    }

    // handle keys when open
    if (key === this.keys.Down) {
      return this.menuActions.Next;
    } else if (key === this.keys.Up) {
      return this.menuActions.Previous;
    } else if (key === this.keys.Home) {
      return this.menuActions.First;
    } else if (key === this.keys.End) {
      return this.menuActions.Last;
    } else if (key === this.keys.Escape) {
      return this.menuActions.Close;
    } else if (key === this.keys.Enter) {
      return this.menuActions.CloseSelect;
    } else if (
      key === this.keys.Backspace ||
      key === this.keys.Clear ||
      key.length === 1
    ) {
      return this.menuActions.Type;
    }
  };

  // get updated option index
  _getUpdatedIndex = (current, max, action) => {
    switch (action) {
      case this.menuActions.First:
        return 0;
      case this.menuActions.Last:
        return max;
      case this.menuActions.Previous:
        return Math.max(0, current - 1);
      case this.menuActions.Next:
        return Math.min(max, current + 1);
      default:
        return current;
    }
  };

  // filter an array of options against an input string
  // returns an array of options that begin with the filter string, case-independent
  _filterOptions = (options = [], filter, exclude = []) => {
    return options.filter((option) => {
      const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0;
      return matches && exclude.indexOf(option) < 0;
    });
  };

  _init = () => {
    if (!this.formEl) {
      console.log("Combobox Missing Form");
      return;
    }

    this.wrapperEl = this.formEl.querySelector("combo-box");
    this.inputEl = this.wrapperEl.querySelector(
      "input[name=rtx_option_plans_dropdown]"
    );
    this.listboxEl = this.wrapperEl.querySelector("list-box");
    this.optionId = this.listboxEl.dataset.identifier;
    this.options = Array.from(this.listboxEl.children);
    /*
      On some pages (example: Subscription Page) there are hidden form inputs that change their values to match
      a combox selection in a different section ( lemmings ). If this is a  combo-box that changes these other inputs on the
      page it will have a data-attribute of lemmings set to true.
    */
    if (this.wrapperEl.dataset.lemmings) {
      this.allLemmings = document.querySelectorAll("[data-combobox-lemming]");
    }

    if (this.options.length === 0) {
      console.log("Combox Error: No Options Found");
      return;
    }

    this.options.forEach((el, index) => {
      el.addEventListener("click", () => this._onOptionClick(index));
    });

    this.inputEl.addEventListener("blur", this._onInputBlur);
    this.inputEl.addEventListener("click", () => this._updateMenuState(true));
    this.inputEl.addEventListener("keydown", (e) => this._onInputKeyDown(e));

    this._onOptionClick(this.activeIndex);

    // Add event listener for clicks outside the combo box
    document.addEventListener("click", this._onDocumentClick);
  };

  _onInputKeyDown = function (event) {
    const { key } = event;
    const max = this.options.length - 1;

    const action = this._getActionFromKey(key, this.open);

    switch (action) {
      case this.menuActions.Next:
      case this.menuActions.Last:
      case this.menuActions.First:
      case this.menuActions.Previous:
        event.preventDefault();
        return this._onOptionChange(
          this._getUpdatedIndex(this.activeIndex, max, action)
        );
      case this.menuActions.CloseSelect:
        event.preventDefault();
        this._selectOption(this.activeIndex);
        return this._updateMenuState(false);
      case this.menuActions.Close:
        event.preventDefault();
        return this._updateMenuState(false);
      case this.menuActions.Open:
        return this._updateMenuState(true);
    }
  };

  _onInputBlur = function () {
    if (this.ignoreBlur) {
      this.ignoreBlur = false;
      return;
    }

    if (this.open) {
      this._updateMenuState(false, false);
    }
  };

  _onOptionChange = function (index) {
    this.activeIndex = index;

    this.inputEl.setAttribute(
      "aria-activedescendant",
      `${this.optionId}__${index}`
    );
    this.inputEl.dataset.index = index;
    this.inputEl.dataset.sellingPlanId =
      this.options[index].dataset.sellingPlanId;
    this.inputEl.value = this.options[index].innerText;

    this.options.forEach((el) => {
      el.classList.remove("option-current");
    });
    this.options[index].classList.add("option-current");

    if (this.priceDisplay) {
      if (this.activeIndex > 0) {
        this.priceDisplay
          .querySelector(".one-time-price-box")
          .classList.add("tw-hidden");
        this.priceDisplay
          .querySelector(".subscription-price-box")
          .classList.remove("tw-hidden");
      } else {
        this.priceDisplay
          .querySelector(".subscription-price-box")
          .classList.add("tw-hidden");
        this.priceDisplay
          .querySelector(".one-time-price-box")
          .classList.remove("tw-hidden");
      }
    }

    /* If combobox changes other selling plan inputs on page, update their values */
    if (this.allLemmings) {
      this.allLemmings.forEach((el) => {
        if (el.dataset.comboboxLemming === "value") {
          el.value = this.options[index].dataset.sellingPlanId;
        }
        if (el.dataset.comboboxLemming === "label") {
          el.innerText = this.options[index].innerText;
        }
      });
    }
  };

  _onOptionClick = function (index) {
    this._onOptionChange(index);
    this._selectOption(index);
    this._updateMenuState(false);
  };

  _onOptionMouseDown = function () {
    this.ignoreBlur = true;
  };

  _selectOption = function (index) {
    const selected = this.options[index];
    this.inputEl.value = selected.innerText;
    this.activeIndex = index;
  };

  _updateMenuState = function (open, callFocus = true) {
    this.open = open;

    this.wrapperEl.setAttribute("aria-expanded", `${open}`);
    open
      ? this.wrapperEl.classList.add("open")
      : this.wrapperEl.classList.remove("open");
  };

  _onDocumentClick = (event) => {
    if (this.open && !this.wrapperEl.contains(event.target)) {
      this._updateMenuState(false);
    }
  };

  _getIndex = () => {
    return this.activeIndex;
  };

  _getSellingPlan = () => {
    return this.inputEl.dataset.sellingPlanId;
  };

  _refresh = (form) => {
    this.formEl = form;
    this._init();
  };
}
